'use client';

import { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';

const ingredients = [
{
  name: 'Cold-Pressed Orange Juice',
  role: 'The Core Engine',
  detail: 'Raw hesperidin and naringenin flavonoids that directly upregulate fat-oxidizing enzymes. Not from concentrate — never heated above 40°C.',
  stat: '240mg',
  statLabel: 'Hesperidin per glass',
  color: '#FF6B1A',
  emoji: '🍊',
  span: 'col-span-2 row-span-2',
  image: "/assets/images/female_drinking_orange_juice_usa.png",
  imageAlt: 'American woman drinking fresh orange juice in a modern USA kitchen',
  large: true
},
{
  name: 'L-Carnitine',
  role: 'Fat Transporter',
  detail: 'Shuttles fatty acids directly into mitochondria for combustion.',
  stat: '500mg',
  statLabel: 'Per serving',
  color: '#FFB347',
  emoji: '⚡',
  span: 'col-span-1 row-span-1',
  large: false
},
{
  name: 'Magnesium Glycinate',
  role: 'Sleep Amplifier',
  detail: 'Deepens slow-wave sleep — the phase where 80% of fat burning occurs.',
  stat: '200mg',
  statLabel: 'Bioavailable form',
  color: '#7C9EFF',
  emoji: '🌙',
  span: 'col-span-1 row-span-1',
  large: false
},
{
  name: 'Vitamin C + Bioflavonoids',
  role: 'Enzyme Catalyst',
  detail: 'Amplifies carnitine synthesis by 60%. More carnitine = more fat burned.',
  stat: '1000mg',
  statLabel: 'Vitamin C complex',
  color: '#FFD080',
  emoji: '✨',
  span: 'col-span-1 row-span-1',
  large: false
},
{
  name: 'Chromium Picolinate',
  role: 'Blood Sugar Stabilizer',
  detail: 'Keeps insulin low overnight — the essential condition for fat mobilization.',
  stat: '200mcg',
  statLabel: 'Bioavailable',
  color: '#FF8C42',
  emoji: '🎯',
  span: 'col-span-1 row-span-1',
  large: false
}];


export default function IngredientsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.1 }
    );
    sectionRef.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right').
    forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Parallax effect on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (imageRef.current) {
        const rect = imageRef.current.getBoundingClientRect();
        const scrollProgress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
        setScrollY(scrollProgress);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial call
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section
      id="ingredients"
      ref={sectionRef}
      className="py-32 px-6 relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #0D0F1A 0%, #100D1A 50%, #0D0F1A 100%)' }}>
      
      {/* bg accent */}
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full blur-[140px] pointer-events-none"
      style={{ background: 'radial-gradient(circle, rgba(255,179,71,0.06) 0%, transparent 70%)' }} />
      

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16 reveal">
          <div>
            <p className="text-citrus font-bold uppercase tracking-[0.3em] text-xs mb-4">What&apos;s Inside</p>
            <h2 className="font-fraunces text-5xl md:text-7xl font-bold tracking-tight leading-[0.9]">
              Five ingredients.
              <br />
              <span className="gradient-text-citrus italic">One result.</span>
            </h2>
          </div>
          <p className="text-cream-muted text-lg max-w-sm leading-relaxed">
            No fillers. No proprietary blends. Every ingredient at a clinically effective dose — transparent label, always.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 auto-rows-auto">
          {/* Large card — cold pressed OJ */}
          <div className="md:col-span-2 md:row-span-2 bento-card glass-card rounded-3xl overflow-hidden reveal-left" style={{ minHeight: '400px' }}>
            <div 
              ref={imageRef}
              className="relative h-56 overflow-hidden"
              style={{
                transform: `translateY(${scrollY * 30}px) scale(${1 + scrollY * 0.1})`,
                transition: 'transform 0.1s ease-out'
              }}
            >
              <AppImage
                src={ingredients[0].image!}
                alt={ingredients[0].imageAlt!}
                fill
                className="object-cover image-fade-scale" />
              
              <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 30%, rgba(13,15,26,0.95) 100%)' }} />
              <div
                className="absolute top-4 left-4 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest"
                style={{ background: `${ingredients[0].color}20`, color: ingredients[0].color, border: `1px solid ${ingredients[0].color}30` }}>
                
                {ingredients[0].role}
              </div>
            </div>
            <div className="p-8">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-2xl font-fraunces font-bold text-cream">{ingredients[0].name}</h3>
                <div className="text-right">
                  <p className="text-3xl font-fraunces font-black" style={{ color: ingredients[0].color }}>{ingredients[0].stat}</p>
                  <p className="text-xs text-muted">{ingredients[0].statLabel}</p>
                </div>
              </div>
              <p className="text-cream-muted leading-relaxed">{ingredients[0].detail}</p>
            </div>
          </div>

          {/* Small cards */}
          {ingredients.slice(1).map((ing, i) =>
          <div
            key={ing.name}
            className={`bento-card glass-card rounded-3xl p-6 reveal`}
            style={{ transitionDelay: `${(i + 1) * 100}ms` }}>
            
              <div className="flex items-start justify-between mb-4">
                <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-xl"
                style={{ background: `${ing.color}15` }}>
                
                  {ing.emoji}
                </div>
                <div
                className="text-xs font-bold uppercase tracking-widest px-2.5 py-1 rounded-full"
                style={{ background: `${ing.color}12`, color: ing.color }}>
                
                  {ing.role}
                </div>
              </div>
              <h3 className="font-fraunces font-bold text-cream text-lg mb-2">{ing.name}</h3>
              <p className="text-muted text-sm leading-relaxed mb-4">{ing.detail}</p>
              <div className="border-t border-white/5 pt-4 flex items-center justify-between">
                <span className="text-xl font-fraunces font-black" style={{ color: ing.color }}>{ing.stat}</span>
                <span className="text-xs text-muted">{ing.statLabel}</span>
              </div>
            </div>
          )}
        </div>

        {/* Trust badges */}
        <div className="mt-12 flex flex-wrap gap-4 justify-center reveal">
          {[
          { icon: '🧪', label: 'Third-Party Tested' },
          { icon: '🌿', label: '100% Natural' },
          { icon: '🚫', label: 'Zero Artificial Sweeteners' },
          { icon: '✅', label: 'NSF Certified' },
          { icon: '🇺🇸', label: 'Made in USA' }].
          map((badge) =>
          <div key={badge.label} className="flex items-center gap-2 px-4 py-2.5 rounded-full glass-card text-sm font-medium text-cream-muted">
              <span>{badge.icon}</span>
              <span>{badge.label}</span>
            </div>
          )}
        </div>
      </div>
    </section>);

}